import React from "react";
import PropTypes from "prop-types";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Button,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  darken,
} from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import { GeneratePrivacyNoticePdf } from "./PrivacyNotice/GeneratePrivacyNoticePDF";
import configSource from "../res/config.json";

// Required by react-pdf to render PDFs without blocking the UI.
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

let privacyPdfPromise = null;

const privacyNoticeCountriesMap = {
  leaseplus: ["uk"],
  upcare: ["uk", "se", "no", "dk", "jp"],
};

export default class ConfidentialityNotice extends React.Component {
  static propTypes = {
    params: PropTypes.object,
    labels: PropTypes.object,
    switchFormState: PropTypes.func,
    customerInformation: PropTypes.object,
    color: PropTypes.string,
  };

  state = {
    verify: false,
    pdfURL: null,
    printFrameLoaded: false,
    printPreviewOpen: false,
    numPages: null,
    pageWidth: null,
  };

  printFrameRef = React.createRef();

  setPreviewContainerRef = (element) => {
    if (
      element &&
      element.clientWidth &&
      element.clientWidth !== this.state.pageWidth
    ) {
      this.setState({ pageWidth: element.clientWidth });
    }
  };

  renderPreviewPages = () => {
    if (!this.state.pageWidth) {
      return null;
    }
    return Array.from({ length: this.state.numPages || 1 }, (_, index) => (
      <Page
        key={index}
        pageNumber={index + 1}
        width={this.state.pageWidth}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    ));
  };

  switchFormState = (value) => {
    this.props.switchFormState(value);
  };

  handleChecked = (event) => {
    let newState;
    switch (event.target.id) {
      case "verify":
        newState = this.state.verify ? false : true;
        this.setState({ verify: newState });
        break;
      default:
        throw Error("Unknown button ID");
    }
  };

  fetchConfidentialityPDF = async () => {
    const country = this.props.params.lang.toLowerCase();
    const pdfName = "confidentiality_message.pdf";
    const response = await fetch(
      configSource.ROOT +
        `getimage?company=hcs&country=${country}&type=${pdfName}`,
    );
    const pdfURL = response.url;
    this.setState({ pdfURL, printFrameLoaded: false });
  };

  generatePrivacyNoticePdf = async () => {
    if (!privacyPdfPromise) {
      privacyPdfPromise = GeneratePrivacyNoticePdf({
        params: this.props.params,
        customerInformation: this.props.customerInformation,
      });
    }

    try {
      const pdfBytes = await privacyPdfPromise;
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const pdfURL = URL.createObjectURL(blob);
      this.setState({ pdfURL, printFrameLoaded: false });
    } catch (error) {
      privacyPdfPromise = null;
      console.error("Failed to generate privacy notice PDF:", error);
    }
  };

  handlePrint = () => {
    const printFrame = this.printFrameRef.current;

    if (!printFrame || !printFrame.contentWindow) {
      return;
    }

    try {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
    } catch (error) {
      this.openPdfInNewTab();
    }
  };

  openPrintPreview = () => {
    this.setState({ printPreviewOpen: true });
  };

  closePrintPreview = () => {
    this.setState({ printPreviewOpen: false });
  };

  openPdfInNewTab = () => {
    window.open(this.state.pdfURL, "_blank", "noopener,noreferrer");
  };

  componentDidMount() {
    const lang = this.props.params.lang.toLowerCase();
    const formType = this.props.params.formType.toLowerCase();

    if (privacyNoticeCountriesMap[formType].includes(lang)) {
      this.generatePrivacyNoticePdf();
    } else {
      this.fetchConfidentialityPDF();
    }
  }
  componentWillUnmount() {
    privacyPdfPromise = null;
  }

  render() {
    const lang = this.props.params.lang.toLowerCase();
    const formType = this.props.params.formType.toLowerCase();
    return (
      <div>
        {this.state.pdfURL ? (
          <div style={{ textAlign: "center" }}>
            <div
              ref={this.setPreviewContainerRef}
              style={{
                width: "75%",
                margin: "0 auto",
                border: "5px solid black",
                ...(this.state.numPages > 1
                  ? {
                      maxHeight: "1252px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }
                  : {}),
              }}
            >
              <Document
                file={this.state.pdfURL}
                onLoadSuccess={({ numPages }) => this.setState({ numPages })}
              >
                {this.renderPreviewPages()}
              </Document>
            </div>
            <iframe
              ref={this.printFrameRef}
              title="Confidentiality notice print frame"
              src={this.state.pdfURL}
              onLoad={() => this.setState({ printFrameLoaded: true })}
              style={{
                border: 0,
                height: 0,
                opacity: 0,
                pointerEvents: "none",
                position: "fixed",
                right: 0,
                top: 0,
                width: 0,
              }}
            />
          </div>
        ) : (
          "Loading PDF..."
        )}
        <Button
          sx={{
            backgroundColor: this.props.color,
            "&:hover": {
              backgroundColor: darken(this.props.color, 0.2), // Darken by 20%
            },
            marginLeft: "10px",
          }}
          onClick={this.openPrintPreview}
          variant="contained"
          disabled={!this.state.pdfURL}
        >
          <PrintRoundedIcon sx={{ marginRight: "5px" }} />
          {this.props.labels.print || "Print"}
        </Button>
        <FormControlLabel
          className="quote_order"
          control={
            <Checkbox
              id="verify"
              checked={this.state.verify}
              onChange={this.handleChecked}
              sx={{ width: "10%" }}
            />
          }
          sx={{ width: "100%" }}
          label={this.props.labels.confidentiality_verify}
        />
        <div className="button-container">
          <div className="button-container-row">
            <Button
              sx={{
                backgroundColor: this.props.color,
                "&:hover": {
                  backgroundColor: darken(this.props.color, 0.2), // Darken by 20%
                },
              }}
              onClick={() => {
                this.switchFormState("edit");
              }}
              variant="contained"
            >
              <ArrowBackRoundedIcon sx={{ marginRight: "5px" }} />
              {this.props.labels.edit_form}
            </Button>

            <Button
              sx={{
                minWidth: "350px",
                minHeight: "45px",
                backgroundColor: this.props.color,
                "&:hover": {
                  backgroundColor: darken(this.props.color, 0.2), // Darken by 20%
                },
                marginLeft:
                  this.props.params.formType.toLowerCase() === "leaseplus"
                    ? "auto"
                    : "0",
              }}
              onClick={() => {
                if (
                  formType === "upcare" &&
                  (lang === "uk" ||
                    lang === "se" ||
                    lang === "no" ||
                    lang === "dk" ||
                    lang === "jp")
                ) {
                  this.switchFormState("payment_info");
                  return;
                }
                this.switchFormState("credit_check");
              }}
              variant="contained"
              disabled={!this.state.verify}
            >
              <ArrowForwardRoundedIcon sx={{ marginLeft: "300px" }} />
              <span className="app-span-text">
                {this.props.labels.preview_form}{" "}
              </span>
            </Button>
          </div>
        </div>
        <Dialog
          open={this.state.printPreviewOpen}
          onClose={this.closePrintPreview}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle sx={{ paddingRight: "48px" }}>
            {this.props.labels.print_preview || "Print preview"}
            <IconButton
              aria-label="Close print preview"
              onClick={this.closePrintPreview}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ height: "80vh", padding: 0 }}>
            {this.state.pdfURL && (
              <object
                data={
                  this.state.pdfURL +
                  "#toolbar=1&navpanes=0&scrollbar=1&amp;view=FitH"
                }
                type="application/pdf"
                width="100%"
                height="100%"
              >
                <p style={{ padding: "16px" }}>
                  Your browser does not support PDFs. Please download the PDF to
                  view it:
                  <a href={this.state.pdfURL}>Download PDF</a>.
                </p>
              </object>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.openPdfInNewTab}>
              <OpenInNewRoundedIcon sx={{ marginRight: "5px" }} />
              {this.props.labels.open_pdf || "Open PDF"}
            </Button>
            <Button
              onClick={this.handlePrint}
              variant="contained"
              disabled={!this.state.printFrameLoaded}
              sx={{
                backgroundColor: this.props.color,
                "&:hover": {
                  backgroundColor: darken(this.props.color, 0.2), // Darken by 20%
                },
              }}
            >
              <PrintRoundedIcon sx={{ marginRight: "5px" }} />
              {this.props.labels.print || "Print"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
